import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

interface MediaItem {
  type: 'image' | 'youtube' | 'mp4';
  src: string;
}

interface SlideshowProps {
  media: (string | MediaItem)[];
}

const getYoutubeEmbedUrl = (url: string) => {
  let videoId = '';
  
  if (url.includes('youtu.be/')) {
    // Handle youtu.be/VIDEO_ID format
    videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
  } else if (url.includes('watch?v=')) {
    // Handle youtube.com/watch?v=VIDEO_ID format
    videoId = url.split('v=')[1]?.split('&')[0] || '';
  } else if (url.includes('/embed/')) {
    // Handle already embedded URLs
    videoId = url.split('/embed/')[1]?.split('?')[0] || '';
  } else {
    // Fallback: try to get the last part of the URL
    videoId = url.split('/').pop()?.split('?')[0] || '';
  }
  
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
  console.log('YouTube URL conversion:', { original: url, videoId, embedUrl });
  return embedUrl;
};

const Slideshow: React.FC<SlideshowProps> = ({ media }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? media.length - 1 : prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
  };

  // Check if current item is a video
  const currentItem = media[currentIndex];
  const currentMediaType = typeof currentItem === 'string' ? 'image' : currentItem.type;
  const isVideo = currentMediaType === 'youtube' || currentMediaType === 'mp4';

  useEffect(() => {
    if (media.length <= 1) return;
    // Don't auto-advance if current item is a video, unless manually paused
    if (isVideo && !isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [media.length, isVideo, isPaused]);

  if (media.length === 0) {
    return <div className="text-center text-gray-500 italic py-8">[ No media available ]</div>;
  }

    const renderMedia = (item: string | MediaItem, index: number) => {
    const isVisible = index === currentIndex;
    
    let mediaItem: MediaItem;
    if (typeof item === 'string') {
      mediaItem = { type: 'image', src: item };
    } else {
      mediaItem = item;
    }
    
    console.log('Rendering media item:', { index, isVisible, mediaItem });

    if (!isVisible) {
      return null; // Don't render hidden items
    }

    switch (mediaItem.type) {
      case 'youtube':
        const embedUrl = getYoutubeEmbedUrl(mediaItem.src);
        return (
          <div key={index} className="absolute inset-0 w-full h-full">
            <iframe
              className="w-full h-full"
              src={embedUrl}
              title={`YouTube Video ${index}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              onLoad={() => console.log('YouTube iframe loaded:', embedUrl)}
              onError={(e) => console.error('YouTube iframe error:', e, embedUrl)}
            ></iframe>
          </div>
        );
      case 'mp4':
        return (
          <div key={index} className="absolute inset-0 w-full h-full">
            <video
              className="w-full h-full object-contain"
              src={mediaItem.src}
              controls
              autoPlay
              muted
              loop
            />
          </div>
        );
      case 'image':
      default:
        return (
          <img
            key={index}
            src={mediaItem.src}
            alt={`Slide ${index}`}
            className="absolute inset-0 w-full h-full object-contain"
            onError={(e) => console.error('Failed to load image:', mediaItem.src)}
            onLoad={() => console.log('Image loaded successfully:', mediaItem.src)}
          />
        );
    }
  };

  return (
    <div className="relative w-full h-96 overflow-hidden rounded-lg bg-gray-900 group">
      {media.map(renderMedia)}
      
      {media.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute top-1/2 left-2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute top-1/2 right-2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>

        </>
      )}
    </div>
  );
};

export default Slideshow; 